import { FocusableOption, FocusMonitor } from '@angular/cdk/a11y';
import { CdkTree, CdkTreeNode } from '@angular/cdk/tree';
import {
  AfterViewInit,
  Directive,
  ElementRef,
  OnDestroy,
  OnInit,
  inject,
  contentChild
} from '@angular/core';
import { MatTreeNodeToggle } from '@angular/material/tree';
import { fromEvent, Subject, takeUntil } from 'rxjs';
import { EspMatTreeDirective } from './esp-tree.directive';
import { EspTreeUnit } from './esp-tree-unit-exstention.interface';

const TREE_NODE = 'MAT-TREE-NODE';
const NESTED_TREE_NODE = 'MAT-NESTED-TREE-NODE';

/**
 * * Holder for the node content
 */
@Directive({
    selector: '[espNodeContent]',
    host: {
        tabindex: '-1',
        class: 'esp-content-focus',
    },
    standalone: true,
})
export class EspNodeContentDirective {
    constructor() {}
}

/**
 * * Base node class for common functionality
 */
@Directive({
    selector: '[esp-tree-node-base]',
    host: {
        '(keydown.enter)': 'clickContent($event)',
        '(keydown.space)': 'clickContent($event)',
    },
    standalone: false
})
export class EspTreeNodeBaseDirective<T extends EspTreeUnit<T>, K>
    implements OnInit, OnDestroy
{
    protected espTree = inject<EspMatTreeDirective<T, K>>(EspMatTreeDirective);
    protected elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    protected _treeNode = inject<CdkTreeNode<T, K>>(CdkTreeNode);
    protected focusMonitor = inject(FocusMonitor);

    protected parentNestedNode: HTMLElement;
    protected destroyed = new Subject<void>();
    readonly content = contentChild(EspNodeContentDirective, { read: ElementRef });

    get getData() {
        return this._treeNode.data;
    }

    ngOnDestroy(): void {
        this.destroyed.next();
        this.destroyed.complete();
    }

    ngOnInit() {
        this.parentNestedNode =
            this.elementRef.nativeElement.parentNode?.parentElement;
        this.monitorNodeFocus();
    }

    /**
     * - Handle tabindex to prevent 'Tab' navigation. Navigation should be handle by the keyManager in 'esp-tree' directive
     * - Add cdk-focus support to receive our default site focus indicator
     */
    private monitorNodeFocus() {
        this.focusMonitor
            .monitor(this.elementRef.nativeElement)
            .pipe(takeUntil(this.destroyed))
            .subscribe((origin) => {
                const tabIndex = origin ? 0 : -1;
                this.elementRef.nativeElement.tabIndex = tabIndex;
            });
    }

    /**
     * Skip node if wrapped in collapsed nested not
     *
     * @returns if current node wrapped in collapsed nested node
     */
    skip(): boolean {
        return (
            this.parentNestedNode.tagName === NESTED_TREE_NODE &&
            this.parentNestedNode.getAttribute('aria-expanded') == 'false'
        );
    }

    /**
     * Set node focus.
     * * set the espTree.currentIndex need for 'ArrowLeft' keyboard event to set the focus on parent element. handled in esp-tree.directive
     *
     * @param origin
     */
    focus(origin?) {
        if (origin) {
            this.elementRef.nativeElement.tabIndex = 0;
            this.elementRef.nativeElement.focus();
            this.espTree.currentIndex = this._treeNode.data.index;
        }
    }

    /**
     * Handle default content keyboard click
     *
     * @param event
     */
    clickContent(event: Event) {
        this.content().nativeElement.click();
        event.stopPropagation();
    }
}

/**
 * ! Accessibility
 * * Directive to enrich material nested node for a11y keyboard support
 */
@Directive({
    selector: 'mat-nested-tree-node',
    host: {
        '(focus)': 'focus($event)',
    },
    providers: [
        { provide: 'espMatNestedTreeNode', useExisting: EspNestedTreeNode },
    ],
    standalone: true,
})
export class EspNestedTreeNode<T extends EspTreeUnit<T>, K = T>
    extends EspTreeNodeBaseDirective<T, K>
    implements AfterViewInit
{
    protected espTree = inject<EspMatTreeDirective<T, K>>(EspMatTreeDirective);
    protected elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    protected _treeNode = inject<CdkTreeNode<T, K>>(CdkTreeNode);
    protected focusMonitor = inject(FocusMonitor);
    private tree = inject<CdkTree<T, K>>(CdkTree);

    readonly toggleButton = contentChild(MatTreeNodeToggle);

    get nodeType() {
        return NESTED_TREE_NODE;
    }

    constructor() {
        super();
    }

    ngAfterViewInit(): void {
        fromEvent(this.elementRef.nativeElement, 'keydown')
            .pipe(takeUntil(this.destroyed))
            .subscribe((event: KeyboardEvent) => {
                const target = event.target as HTMLElement;
                const targetEvent = target === this.elementRef.nativeElement;
                switch (event.key) {
                    case 'ArrowRight':
                        if (
                            targetEvent &&
                            !this.tree.treeControl.isExpanded(
                                this._treeNode.data,
                            )
                        ) {
                            this.tree.treeControl.expand(this._treeNode.data);
                            event.stopPropagation();
                        } else if (
                            !targetEvent &&
                            target.tagName != NESTED_TREE_NODE
                        ) {
                            event.stopPropagation();
                        }
                        break;
                    case 'ArrowLeft':
                        if (targetEvent) {
                            if (
                                this.tree.treeControl.isExpanded(
                                    this._treeNode.data,
                                )
                            ) {
                                this.tree.treeControl.collapse(
                                    this._treeNode.data,
                                );
                            } else {
                                this.parentNestedNode.focus();
                            }
                        } else {
                            this.focus(event);
                        }
                        event.stopPropagation();
                        break;
                    default:
                        break;
                }
            });
    }
}

/**
 * ! Accessibility
 * * Directive to enrich material node for a11y keyboard support
 * set the `aria-expanded` to undefined to prevent assisitive technologies from mark it as collapse
 */
@Directive({
    selector: 'mat-tree-node',
    exportAs: 'matTreeNode',
    host: {
        '[attr.aria-expanded]': 'undefined',
    },
    providers: [{ provide: 'espMatTreeNode', useExisting: EspMatTreeNode }],
    standalone: true,
})
export class EspMatTreeNode<T extends EspTreeUnit<T>, K = T>
    extends EspTreeNodeBaseDirective<T, K>
    implements OnInit, FocusableOption
{
    protected espTree = inject<EspMatTreeDirective<T, K>>(EspMatTreeDirective);
    protected elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    protected _treeNode = inject<CdkTreeNode<T, K>>(CdkTreeNode);
    protected focusMonitor = inject(FocusMonitor);

    get nodeType() {
        return TREE_NODE;
    }

    constructor() {
        super();
    }
}
